import _                                        from 'underscore';
import classNames                               from 'classnames';
import React, { CSSProperties, FC, useContext } from 'react';

import { Button, Modal, ModalProps }                  from 'semantic-ui-react';
import { DynamicComponentsContext }                   from '../../contexts';
import { LoadingIndicator }                           from '../util/loading/LoadingIndicator';
import { Flexbox, FlexItem }                          from '../../containers';
import { ClassValue }                                 from 'classnames/types';
import { Align, FlexAlign, FlexJustify, KeyedObject } from '../../../types';
import { TogglableContentHook }                       from '../../../hooks/useTogglableContentReducer';
import { ToastsHook }                                 from '../../../hooks';

export interface BasicDialogProps {
	hasSystemContext?: boolean,
	operationDisabled?: boolean,
	operationIsDangerous?: boolean,
	loading?: boolean,
	scrolling?: boolean,
	autoClose?: boolean,
	showCancelButton?: boolean,
	showSubmitButton?: boolean,
	cancelLabel?: string,
	submitLabel?: string,
	successToastLabel?: string,
	errorToastLabel?: string,
	loadingMessage?: string,
	className?: ClassValue,
	controlAlign?: Align,
	headerComponent?: any,
	styles?: CSSProperties,

	onComplete?( data: any ): void,

	onError?( data: any ): void,

	onClose?(): void,

	onSubmit?(): any,

	[ additionalProps: string ]: any
}

const defaultProps: BasicDialogProps = {
	hasSystemContext:     true,
	operationDisabled:    false,
	operationIsDangerous: false,
	loading:              false,
	scrolling:            false,
	autoClose:            true,
	showCancelButton:     true,
	showSubmitButton:     true,
	cancelLabel:          'Cancel',
	submitLabel:          'Submit',
	successToastLabel:    'Success',
	errorToastLabel:      'Operation Failed',
	loadingMessage:       'Loading...',
	className:            'padding-lg',
	controlAlign:         Align.RIGHT,
	headerComponent:      null,
	styles:               {},

	onComplete: () => {},
	onError:    () => {},
	onClose:    () => {},
	onSubmit:   () => {},
}

const BasicDialog: FC<BasicDialogProps> = React.memo<BasicDialogProps>( ( props ) => {

	let { toasts: [ , toastActions ] } = props.hasSystemContext ? useContext( DynamicComponentsContext ) : { toasts: [ {}, {} ] as ToastsHook },
	    { modal: [ , modalActions ] }  = props.hasSystemContext ? useContext( DynamicComponentsContext ) : { modal: [ {}, {} ] as TogglableContentHook<ModalProps> };

	let contentClasses = classNames( props.className );

	const handleOnClose = () => {

		if( props.hasSystemContext && props.autoClose ) modalActions.toggle( false );

		props.onClose();
	};

	const handleOnSubmit = async () => {

		try{

			return Promise.resolve( props.onSubmit() )
			              .then( handleOnComplete );

		} catch( e ) {

			if( props.hasSystemContext ){
				toastActions.error( 'Network Issue', e.message, { timeoutMS: 0 } );
			}

			props.onError( e );
		}
	};

	const handleOnComplete = ( res: any ) => {

		if( _.isUndefined( res ) || !res.errors ){

			if( props.hasSystemContext && props.successToastLabel ){
				toastActions.success( props.successToastLabel );
			}

			props.onComplete( res );

		} else {

			if( props.hasSystemContext && props.errorToastLabel ){
				toastActions.error( props.errorToastLabel, res.errors, { timeoutMS: 0 } );
			}

			props.onError( res );

		}
	};

	// Loading...
	if( props.loading ){
		return (
			<div className={ contentClasses }>
				<LoadingIndicator className={ 'padding-lg' } label={ props.loadingMessage } isActive={ true } inverted/>
			</div>
		);
	}

	// Content...
	let hasHeader          = !_.isNull( props.headerComponent ),
	    modalContentStyles = {
		    borderTopLeftRadius:  '0.2857rem',
		    borderTopRightRadius: '0.2857rem',
		    overflow:             props.scrolling ? 'auto' : 'hidden',
		    ...props.styles
	    };

	return (
		<>
			{ props.headerComponent }

			<Modal.Content scrolling={ props.scrolling } className={ contentClasses } style={ !hasHeader ? modalContentStyles : {} }>

				{ props.children }

			</Modal.Content>
			<Modal.Actions className={ `text-${ props.controlAlign }` }>

				<Flexbox align={ FlexAlign.CENTER }
				         justify={ props.controlAlign === Align.LEFT ? FlexJustify.START :
				                   props.controlAlign === Align.RIGHT ? FlexJustify.END : FlexJustify.CENTER }>

					<FlexItem noGrow>

						{ props.showCancelButton && (
							<Button key='close'
							        basic={ true }
							        content={ props.cancelLabel }
							        onClick={ handleOnClose }/>
						) }

					</FlexItem>
					<FlexItem noGrow>

						{ props.showSubmitButton && (
							<Button key='operation'
							        content={ props.submitLabel }
							        primary={ !props.operationIsDangerous }
							        negative={ props.operationIsDangerous }
							        disabled={ props.operationDisabled }
							        onClick={ handleOnSubmit }/>
						) }

					</FlexItem>
				</Flexbox>

			</Modal.Actions>
		</>
	)
} );

BasicDialog.defaultProps = defaultProps;

export { BasicDialog };
